import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc, updateDoc, runTransaction } from "firebase/firestore";
import { db } from "../../firebase";
import { buildAffiliateTurnoverUpdate } from "../../lib/affiliateBonus";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const emptyBoard = Array(9).fill(null);

const lines = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function getWinner(board) {
  for (const [a, b, c] of lines) {
    if (board[a] && board[a] === board[b] && board[a] === board[c])
      return board[a];
  }
  return null;
}

function shortName(name) {
  if (!name) return "";
  if (window.innerWidth < 500) {
    // Show first 5 letters only
    return name.length > 5 ? name.slice(0, 5) + "…" : name;
  }
  // Desktop: show up to first + last initial
  const [first, last] = name.split(" ");
  if (last) {
    return first + " " + last[0] + ".";
  }
  return name;
}

function isFull(board) {
  return board.every(Boolean);
}

// --- MINIMAX for O (computer) ---
function minimax(board, depth, isMaximizing) {
  const winner = getWinner(board);
  if (winner === "O") return { score: 1 };
  if (winner === "X") return { score: -1 };
  if (isFull(board)) return { score: 0 };

  let best;
  if (isMaximizing) {
    best = { score: -Infinity };
    for (let i = 0; i < 9; i++) {
      if (!board[i]) {
        board[i] = "O";
        let evalResult = minimax(board, depth + 1, false);
        board[i] = null;
        if (evalResult.score > best.score) {
          best = { score: evalResult.score, move: i };
        }
      }
    }
  } else {
    best = { score: Infinity };
    for (let i = 0; i < 9; i++) {
      if (!board[i]) {
        board[i] = "X";
        let evalResult = minimax(board, depth + 1, true);
        board[i] = null;
        if (evalResult.score < best.score) {
          best = { score: evalResult.score, move: i };
        }
      }
    }
  }
  return best;
}

function playSound(url) {
  const audio = new window.Audio(url);
  audio.volume = 0.55;
  audio.currentTime = 0;
  audio.play().catch(() => {});
}
function botMove(board) {
  playSound("/sounds/bot-play.mp3");
  return minimax(board, 0, true).move;
}

export default function XOGameVsComputer({ onBack, user, betAmount }) {
  const [board, setBoard] = useState([...emptyBoard]);
  const [winner, setWinner] = useState(null);
  const [turn, setTurn] = useState("X");
  const [payoutDone, setPayoutDone] = useState(false);

  const feeDeducted = useRef(false);

  // Bot move effect (after user)
  useEffect(() => {
    if (winner || turn !== "O") return;
    const t = setTimeout(() => {
      const idx = botMove([...board]);
      if (idx === undefined) return;
      const newBoard = [...board];
      newBoard[idx] = "O";
      setBoard(newBoard);

      const win2 = getWinner(newBoard);
      if (win2) setWinner(win2);
      else if (isFull(newBoard)) setWinner("draw");
      setTurn("X");
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [turn, board, winner]);

  useEffect(() => {
    if (!winner || payoutDone) return;
    if (!user?.uid) return;
    const userRef = doc(db, "users", user.uid);

    const handlePayout = async () => {
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        if (!snap.exists()) return;

        const data = snap.data() || {};
        let bal = Number(data.balance || 0);
        const currentTurnover = Number(data.turnoverCompleted || 0);
        const affiliateUpdate = buildAffiliateTurnoverUpdate(data, betAmount);

        if (winner === "X") {
          const prize = +(betAmount * 1.8).toFixed(2);
          bal += prize;
          transaction.update(userRef, {
            balance: bal,
            turnoverCompleted: currentTurnover + betAmount,
            ...affiliateUpdate.fields,
          });
        } else if (winner === "draw") {
          bal += betAmount;
          transaction.update(userRef, { balance: bal });
        } else {
          transaction.update(userRef, {
            turnoverCompleted: currentTurnover + betAmount,
            ...affiliateUpdate.fields,
          });
        }
      });

      if (winner === "X") {
        const prize = +(betAmount * 1.8).toFixed(2);
        toast.success(`You beat the computer! +${prize} USDT credited. 🏆`);
        playSound("/sounds/win.mp3");
      } else if (winner === "draw") {
        toast.success(`Draw! ${betAmount} USDT refunded.`);
        playSound("/sounds/draw.mp3");
      } else {
        toast("Bot wins! Try again.");
        playSound("/sounds/lose.mp3");
      }
      setPayoutDone(true);
    };
    handlePayout();
  }, [winner, user, betAmount, payoutDone]);

  const handleMove = (idx) => {
    if (board[idx] || winner || turn !== "X") return;
    const newBoard = [...board];
    newBoard[idx] = "X";
    setBoard(newBoard);

    playSound("/sounds/xo-click.mp3");

    const win = getWinner(newBoard);
    if (win) {
      setWinner(win);
      return;
    }
    if (isFull(newBoard)) {
      setWinner("draw");
      return;
    }

    setTurn("O");
  };

  return (
    <motion.div
      className="mt-14 flex w-full flex-col items-center gap-8 px-4 md:mt-20"
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", duration: 0.8 }}
    >
      <motion.h2
        className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tight"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.08, type: "spring" }}
      >
        You vs Computer
      </motion.h2>
      {/* Players */}
      <div className="mb-2 flex flex-wrap items-center justify-center gap-4 md:gap-12">
        {/* Player chip */}
        <motion.div
          whileHover={{ scale: 1.08, boxShadow: "0 0 0 4px #bef26499" }}
          className="flex flex-row items-center px-6 py-2 rounded-full bg-lime-400/90 shadow-xl min-w-[90px] max-w-[150px] transition-all"
        >
          <span
            className="text-lg font-bold text-white truncate"
            style={{ maxWidth: "70px" }} // keep chip tight
            title={user.displayName}
          >
            {shortName(user.displayName || "You")}
          </span>
          <span className="text-2xl font-black text-white ml-2">X</span>
        </motion.div>
        {/* Computer chip */}
        <motion.div
          whileHover={{ scale: 1.05, boxShadow: "0 0 0 4px #fde04755" }}
          className="flex flex-row items-center px-6 py-2 rounded-full bg-slate-800/90 shadow-lg min-w-[90px] max-w-[140px]"
        >
          <span
            className="text-lg font-bold text-yellow-300 truncate"
            style={{ maxWidth: "70px" }}
          >
            Computer
          </span>
          <span className="text-2xl font-black text-yellow-300 ml-2">O</span>
        </motion.div>
      </div>
      {/* Board */}
      <motion.div
        className="grid aspect-square w-full max-w-[330px] grid-cols-3 gap-2 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-lime-900 px-3 pb-4 pt-3 shadow-2xl sm:gap-3 sm:px-5 sm:pb-10 sm:pt-5"
        initial={{ scale: 0.94 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.16, type: "spring" }}
      >
        {board.map((cell, i) => {
          const rounded =
            i === 0
              ? "rounded-tl-3xl"
              : i === 2
              ? "rounded-tr-3xl"
              : i === 6
              ? "rounded-bl-3xl"
              : i === 8
              ? "rounded-br-3xl"
              : "rounded-none";
          return (
            <motion.button
              key={i}
              onClick={() => handleMove(i)}
              whileTap={{ scale: cell || winner || turn !== "X" ? 1 : 0.9 }}
              whileHover={{
                backgroundColor:
                  !cell && !winner && turn === "X"
                    ? "rgba(190, 242, 100, 0.13)"
                    : undefined,
              }}
              className={`aspect-square w-full text-4xl font-black shadow transition-all duration-200 sm:text-5xl
                ${rounded}
                ${
                  cell === "X"
                    ? "text-lime-400"
                    : cell === "O"
                    ? "text-yellow-400"
                    : "text-slate-300"
                }
                bg-[rgba(36,42,55,0.88)]`}
              disabled={!!cell || !!winner || turn !== "X"}
            >
              <motion.span
                initial={{ scale: 0.6, opacity: 0 }}
                animate={
                  cell ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }
                }
                transition={{ type: "spring", stiffness: 320, damping: 22 }}
              >
                {cell}
              </motion.span>
            </motion.button>
          );
        })}
      </motion.div>
      {/* Game status */}
      <motion.div
        className="text-lg text-white min-h-[36px] flex items-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        {winner ? (
          winner === "draw" ? (
            <span className="text-yellow-300 font-semibold animate-pulse">
              It's a draw!
            </span>
          ) : winner === "O" ? (
            <span className="text-pink-400 font-bold animate-bounce">
              Computer wins! 😈
            </span>
          ) : (
            <span className="text-lime-300 font-bold animate-bounce">
              You win! (Shouldn’t happen!)
            </span>
          )
        ) : turn === "X" ? (
          <motion.span
            key="your-turn"
            className="text-lime-400 font-bold"
            initial={{ opacity: 0.5, y: 7 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 160 }}
          >
            Your turn
          </motion.span>
        ) : (
          <span className="text-slate-400">Computer thinking...</span>
        )}
      </motion.div>
      <motion.button
        className="mt-2 px-7 py-2 rounded-full bg-slate-800 hover:bg-slate-900 text-lime-400 font-semibold shadow transition"
        onClick={onBack}
        whileHover={{ scale: 1.04, backgroundColor: "#d1fae5" }}
        whileTap={{ scale: 0.96 }}
      >
        Go Back to Lobby
      </motion.button>
    </motion.div>
  );
}
